// Modules
import { createApi } from '@reduxjs/toolkit/query/react';
import { axiosBaseQuery } from 'config/axiosBaseQuery';
import { endpoints } from 'config/api';

// Utils
import {
  Proposal,
  PaginatedProposalsResponse,
  ProposalQueryParams,
  CreateProposalDto,
  UpdateQuoteDto,
  UpdateQuoteStatusDto,
  AddLineItemDto,
  ReorderLineItemsDto,
  ProposalImageUrlResponse,
} from './proposalsTypes';

export const proposalsApi = createApi({
  reducerPath: 'proposalsApi',
  baseQuery: axiosBaseQuery(),
  tagTypes: ['Proposals', 'Proposal', 'ProposalsByLead'],
  endpoints: (builder) => ({
    // Get all proposals with optional filtering and pagination
    fetchProposals: builder.query<PaginatedProposalsResponse, ProposalQueryParams | void>({
      query: (params) => ({
        url: endpoints.proposals.base,
        method: 'GET',
        params,
      }),
      transformResponse: (response: PaginatedProposalsResponse) => response,
      providesTags: ['Proposals'],
    }),

    // Get proposal by ID
    getProposal: builder.query<Proposal, string>({
      query: (id) => ({
        url: `${endpoints.proposals.base}/${id}`,
        method: 'GET',
      }),
      transformResponse: (response: Proposal) => response,
      providesTags: (_res, _err, id) => [{ type: 'Proposal', id }],
    }),

    // Get proposals by lead ID
    getProposalsByLead: builder.query<Proposal[], string>({
      query: (leadId) => ({
        url: `${endpoints.proposals.base}/lead/${leadId}`,
        method: 'GET',
      }),
      transformResponse: (response: Proposal[]) => response,
      providesTags: (_res, _err, leadId) => [
        { type: 'ProposalsByLead', id: leadId },
      ],
    }),

    // Create proposal
    createProposal: builder.mutation<Proposal, CreateProposalDto>({
      query: (data) => ({
        url: endpoints.proposals.base,
        method: 'POST',
        data,
      }),
      transformResponse: (response: Proposal) => response,
      invalidatesTags: (result, error, arg) => [
        'Proposals',
        { type: 'ProposalsByLead', id: arg.leadId },
      ],
    }),

    // Update proposal
    updateProposal: builder.mutation<Proposal, { id: string; data: UpdateQuoteDto }>({
      query: ({ id, data }) => ({
        url: `${endpoints.proposals.base}/${id}`,
        method: 'PUT',
        data,
      }),
      transformResponse: (response: Proposal) => response,
      invalidatesTags: (res) => [
        { type: 'Proposal', id: res?._id },
        'Proposals',
        'ProposalsByLead',
      ],
    }),

    // Update proposal status
    updateProposalStatus: builder.mutation<Proposal, { id: string; data: UpdateQuoteStatusDto }>({
      query: ({ id, data }) => ({
        url: `${endpoints.proposals.base}/${id}/status`,
        method: 'PATCH',
        data,
      }),
      transformResponse: (response: Proposal) => response,
      invalidatesTags: (res) => [
        { type: 'Proposal', id: res?._id },
        'Proposals',
        'ProposalsByLead',
      ],
    }),

    // Delete proposal
    deleteProposal: builder.mutation<void, string>({
      query: (id) => ({
        url: `${endpoints.proposals.base}/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Proposals'],
    }),

    // Duplicate proposal
    duplicateProposal: builder.mutation<Proposal, string>({
      query: (id) => ({
        url: `${endpoints.proposals.base}/${id}/duplicate`,
        method: 'POST',
      }),
      transformResponse: (response: Proposal) => response,
      invalidatesTags: ['Proposals'],
    }),

    // Resend quote email
    resendQuote: builder.mutation<{ success: boolean; email?: string }, string>({
      query: (id) => ({
        url: `${endpoints.proposals.base}/${id}/resend`,
        method: 'POST',
      }),
      invalidatesTags: ['Proposals', 'ProposalsByLead'],
    }),

    // Line Item Management
    addLineItem: builder.mutation<Proposal, { id: string; lineItem: AddLineItemDto }>({
      query: ({ id, lineItem }) => ({
        url: `${endpoints.proposals.base}/${id}/line-items`,
        method: 'POST',
        data: lineItem,
      }),
      transformResponse: (response: Proposal) => response,
      invalidatesTags: (_res, _err, { id }) => [
        { type: 'Proposal', id },
      ],
    }),

    removeLineItem: builder.mutation<Proposal, { id: string; lineItemId: string }>({
      query: ({ id, lineItemId }) => ({
        url: `${endpoints.proposals.base}/${id}/line-items/${lineItemId}`,
        method: 'DELETE',
      }),
      transformResponse: (response: Proposal) => response,
      invalidatesTags: (_res, _err, { id }) => [
        { type: 'Proposal', id },
      ],
    }),

    reorderLineItems: builder.mutation<Proposal, { id: string; reorder: ReorderLineItemsDto }>({
      query: ({ id, reorder }) => ({
        url: `${endpoints.proposals.base}/${id}/line-items/reorder`,
        method: 'PATCH',
        data: reorder,
      }),
      transformResponse: (response: Proposal) => response,
      invalidatesTags: (_res, _err, { id }) => [
        { type: 'Proposal', id },
      ],
    }),

    // Image Upload Endpoints
    uploadIntroductionImage: builder.mutation<Proposal, { id: string; file: File }>({
      query: ({ id, file }) => {
        const formData = new FormData();
        formData.append('file', file);
        return {
          url: `${endpoints.proposals.base}/${id}/introduction/image`,
          method: 'POST',
          data: formData,
          headers: { 'Content-Type': 'multipart/form-data' },
        };
      },
      transformResponse: (response: Proposal) => response,
      invalidatesTags: (_res, _err, { id }) => [
        { type: 'Proposal', id },
      ],
    }),

    uploadLineItemImage: builder.mutation<Proposal, { id: string; lineItemId: string; file: File }>({
      query: ({ id, lineItemId, file }) => {
        const formData = new FormData();
        formData.append('file', file);
        return {
          url: `${endpoints.proposals.base}/${id}/line-items/${lineItemId}/image`,
          method: 'POST',
          data: formData,
          headers: { 'Content-Type': 'multipart/form-data' },
        };
      },
      transformResponse: (response: Proposal) => response,
      invalidatesTags: (_res, _err, { id }) => [
        { type: 'Proposal', id },
      ],
    }),

    getImageUrl: builder.query<ProposalImageUrlResponse, { id: string; imageKey: string }>({
      query: ({ id, imageKey }) => ({
        url: `${endpoints.proposals.base}/${id}/images`,
        method: 'GET',
        params: { key: imageKey },
      }),
      transformResponse: (response: ProposalImageUrlResponse) => response,
    }),
  }),
});

export const {
  useFetchProposalsQuery,
  useGetProposalQuery,
  useGetProposalsByLeadQuery,
  useCreateProposalMutation,
  useUpdateProposalMutation,
  useUpdateProposalStatusMutation,
  useDeleteProposalMutation,
  useDuplicateProposalMutation,
  useResendQuoteMutation,
  useAddLineItemMutation,
  useRemoveLineItemMutation,
  useReorderLineItemsMutation,
  useUploadIntroductionImageMutation,
  useUploadLineItemImageMutation,
  useGetImageUrlQuery,
  useLazyGetImageUrlQuery,
} = proposalsApi;

